<template>
  <div v-cloak>
    <transition name="fade" appear appear-active-class="fade-enter-active">
      <div>
        <div v-if="apiLoaded" class="wrap">
          <a @click="$router.go(-1)" class="back-link">
            <v-icon class="icon-item icon-chevron-left"></v-icon>
            Назад
          </a>
          <v-row>
            <v-col cols="9">
              <h1 class="mb-0">Акт комиссионного обследования</h1>
              <h3>{{ getRegionNameByCode(act.regionCode) }}</h3>
              <h5>{{ getCznNameById(act.cznId) }}</h5>
            </v-col>
            <v-col cols="3">
              <h1 class="mb-3">
                <div class="badge" style="width: 100%">
                  {{ actStatuses[act.status] }}
                </div>
              </h1>
              <div class="text-right">
                <v-dialog
                    v-model="exportDialog"
                    max-width="650px"
                    persistent
                >
                  <template v-slot:activator="{ on }">
                    <div v-on="on" style="text-align: right">
                      <v-btn small outlined class="mb-3">
                        <v-icon class="mr-2" dense>
                          mdi-tray-arrow-up
                        </v-icon>
                        Экспорт
                      </v-btn>
                    </div>
                  </template>

                  <StandardActExportDialog
                      :act="act"
                      :config="config"
                      @close="exportDialog = false"
                  />
                </v-dialog>
              </div>
            </v-col>
          </v-row>

          <v-simple-table class="checklist-table mt-6">
            <template v-slot:default>
              <thead>
              <tr>
                <th style="width:50px">№</th>
                <th>Требования</th>
                <th style="width:100px" class="text-center">Соответствует</th>
                <th style="width:100px" class="text-center">Частично соответствует</th>
                <th style="width:100px" class="text-center">Не соответствует</th>
                <th style="min-width:200px">Комментарий</th>
              </tr>
              </thead>
              <tbody v-for="(section, sectionIndex) in config.sections" :key="section.id">
              <tr class="section-row">
                <td>{{ sectionIndex + 1 }}</td>
                <td colspan="5">{{ section.name }}</td>
              </tr>

              <tr v-for="(indicator, indicatorIndex) in section.indicators" :key="indicator.id">
                <td>{{ sectionIndex + 1 }}.{{ indicatorIndex + 1 }}</td>
                <td>{{ indicator.name }}</td>

                <td colspan="3" class="text-center">
                  <v-radio-group
                      v-model="values[indicator.id]"
                      :disabled="!editable()"
                      class="mb-0"
                  >
                    <div class="checklist-radio">
                      <v-radio value="0" />
                      <v-radio value="1" />
                      <v-radio value="2" class="mb-2" />
                    </div>
                  </v-radio-group>
                </td>

                <td>
                  <v-textarea
                      v-model="comments[indicator.id]"
                      :disabled="!editable() || !values[indicator.id] || values[indicator.id] === '0'"
                      class="py-2"
                      rows="2"
                      dense
                      placeholder="Комментарий"
                      :error="commentError(indicator.id)"
                      :error-messages="commentError(indicator.id) ? ['Заполните комментарий'] : []"
                  />
                </td>
              </tr>
              </tbody>
            </template>
          </v-simple-table>

          <v-row class="mt-4 justify-end">
            <v-col cols="3">
              <div data-field="act-date">
                <DateField
                    labelName="Дата проведения"
                    :dateValue="date"
                    :disabled="!editable()"
                    @minput="date = $event"
                />
                <div v-if="validation.date" class="error-text mt-1">
                  Укажите дату проведения
                </div>
              </div>
            </v-col>
            <v-col cols="6"></v-col>
            <v-col cols="3" style="text-align: right">
              <template v-if="autoPercent">
                <v-text-field
                    label="Процент соответствия"
                    :value="calculatePercent()"
                    :disabled="!editable()"
                    readonly
                    dense
                    suffix="%"
                />
                <v-btn
                    @click="autoPercent = false"
                    :disabled="!editable()"
                    text
                    small
                >
                  Указать вручную
                </v-btn>
              </template>
              <template v-else>
                <v-text-field
                    label="Процент соответствия"
                    v-model="percent"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    dense
                    suffix="%"
                    @keydown="e => (e.key === ',' || e.code === 'NumpadComma') && e.preventDefault()"
                    :disabled="!editable()"
                    :rules="percentRules"
                />
                <v-btn
                    @click="autoPercent = true"
                    :disabled="!editable()"
                    text
                    small
                    class="mt-2"
                >
                  Рассчитать автоматически
                </v-btn>
              </template>
            </v-col>
          </v-row>

          <div>
            <h3 class="mt-6 mb-0">Подтверждающие документы</h3>
            <div v-if="attachments.length && attachmentsModifiedDate" class="mb-3">
              Дата загрузки: {{ attachmentsModifiedDate | dateFormat }}
            </div>
            <v-row class="file-row align-center" v-for="attachment in attachments" :key="attachment.id">
              <v-col cols="12" lg="4">
                <a @click="downloadAttachment(attachment)" download class="file-link">
                  <v-icon class="icon-item icon-file-document-outline"></v-icon>
                  {{ truncateStr(attachment.name, 50) }}
                </a>
              </v-col>
              <v-col cols="12" lg="5" class="d-flex align-center" v-if="editable()">
                <div class="delete ml-2" @click="deleteAttachment(attachment)">
                  <v-icon class="icon-item icon-delete-outline"></v-icon>
                </div>
              </v-col>
            </v-row>

            <template v-if="editable()">
              <input type="file" ref="attachmentInput" style="display: none" @change="addAttachment()">
              <a @click="$refs['attachmentInput'].click()" class="configAdd">+ добавить файл</a>
            </template>
          </div>

          <div class="buttons">
            <v-btn class="button-cancel" @click="$router.push('/standard/')">
              Назад
            </v-btn>

            <v-btn
                class="button-request"
                @click="save"
                v-if="editable()"
            >
              {{ attachments.length ? 'Зафиксировать' : 'Сохранить' }}
            </v-btn>
          </div>
        </div>
      </div>
    </transition>
    <LoaderDialog :show="loading" msg="Загрузка"></LoaderDialog>
    <v-snackbar
        v-model="snackbar"
        :timeout="1500"
        text
        centered
        :color="snackbarError ? 'red' : 'green'"
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
            color="blue"
            text
            small
            v-bind="attrs"
            @click="snackbar = false"
        >
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>

import StandardActExportDialog from "@/components/standard_new/StandardActExportDialog.vue";
import {actStatuses} from "@/modules/ApplicationRepository";
import {getFormById} from "@/modules/api.forms";
import {getActiveDomainConfigs} from "@/modules/api.configs";
import {getIndicatorsByIndicatorFormId} from "@/modules/api.indicators";
import {downloadFile} from "@/modules/api.files";
import LoaderDialog from "@/components/elements/LoaderDialog.vue";
import {saveIndicators} from "@/modules/api.indicators";
import {uploadFile} from "@/modules/api.files";
import {saveForm} from "@/modules/api.forms";
import DateField from "../elements/DateField.vue";

export default {
  name: 'StandardRequest',
  components: {
    DateField,
    LoaderDialog,
    StandardActExportDialog,
  },
  data: () => ({
    act: null,
    actValues: [],
    actStatuses: actStatuses,
    loading: false,
    snackbar: false,
    snackbarText: null,
    snackbarError: false,
    exportDialog: false,
    domain: 'standard_act',
    config: null,
    apiLoaded: false,
    values: {},
    comments: {},
    attachments: [],
    attachmentsModifiedDate: null,
    date: null,
    percent: null,
    percentRules: [
      v => v !== null && v !== '' || 'Укажите процент',
      v => parseFloat(v) >= 0 && parseFloat(v) <= 100 || 'От 0 до 100',
    ],
    autoPercent: true,
    validation: {
      comments: {},
      date: false,
      percent: false,
    },
  }),
  methods: {
    async save() {
      if (!this.validateForm()) {
        this.openSnackbar('Пожалуйста, заполните обязательные поля', true);
        return;
      }

      this.loading = true;
      try {
        const valueIndicators = Object.entries(this.values).map(([indicatorId, value]) => this.createIndicator(indicatorId, value, 'VALUE'));
        const commentIndicators = Object.entries(this.comments).map(([indicatorId, value]) => this.createIndicator(indicatorId, value, 'COMMENT'));
        const fileIndicator = this.createIndicator(this.config.fileIndicatorId, JSON.stringify(this.attachments), 'FILES');
        const dateIndicator = this.createIndicator(this.config.dateIndicatorId, this.date, 'VALUE');
        const percentIndicator = this.createIndicator(this.config.percentIndicatorId, this.autoPercent ? null : this.percent, 'VALUE');
        await saveIndicators([
          ...valueIndicators,
          ...commentIndicators,
          fileIndicator,
          dateIndicator,
          percentIndicator
        ]);

        this.act.status = this.attachments.length ? 'FIXED' : 'DRAFT';
        await saveForm(this.act);

        this.openSnackbar('Данные сохранены');
      } catch (e) {
        console.error(e);
        this.openSnackbar('Ошибка сохранения данных', true);
      } finally {
        this.loading = false;
      }
    },
    createIndicator(indicatorId, value, type) {
      return {
        indicatorId,
        indicatorFormId: this.act.id,
        value,
        type,
        cznId: this.act.cznId,
        regionCode: this.act.regionCode,
      }
    },
    async initialize() {
      this.apiLoaded = false
      this.loading = true

      const actId = this.$route.params.id
      this.act = (await getFormById(actId)).payload
      this.actValues = (await getIndicatorsByIndicatorFormId(actId)).payload

      const config = (await getActiveDomainConfigs(this.domain)).payload[0];
      this.config = { id: config.id, ...config.content };

      this.actValues.forEach(value => {
        if (value.indicatorId === this.config.fileIndicatorId) {
          this.attachments = JSON.parse(value.value);
          this.attachmentsModifiedDate = value.updatedDateTime;
        } else if (value.indicatorId === this.config.dateIndicatorId) {
          this.date = value.value;
        } else if (value.indicatorId === this.config.percentIndicatorId) {
          this.autoPercent = false;
          this.percent = value.value;
        } else {
          if (value.type === 'VALUE') {
            this.$set(this.values, value.indicatorId, value.value);
          }
          if (value.type === 'COMMENT') {
            this.$set(this.comments, value.indicatorId, value.value);
          }
        }
      })

      this.loading = false
      this.apiLoaded = true
    },
    editable() {
      return this.act.status === 'DRAFT';
    },
    resetComment(indicatorId) {
      this.$set(this.comments, indicatorId, '');
    },
    calculatePercent() {
      const totalQuestions = this.config.sections.reduce((total, section) => total + section.indicators.length, 0);
      const zeroAnswers = this.config.sections.reduce((count, section) => {
        return count + section.indicators.filter(indicator => this.values[indicator.id] === '0').length;
      }, 0);

      return totalQuestions ? ((zeroAnswers / totalQuestions) * 100).toFixed(2) : 0;
    },
    truncateStr(str, n) {
      if (str.length > n) {
        return str.substring(0, n - 3) + "..." + str.substring(str.length - 6, str.length)
      }
      return str
    },
    getRegionNameByCode(code) {
      return this.$cznDepartmentsByRegionList.find(r => r.code === code)?.name
    },
    getCznNameById(id) {
      return this.$cznDepartmentsByRegionList.flatMap(r => r.departments).find(d => d.id === id)?.name
    },
    async addAttachment() {
      const input = this.$refs['attachmentInput'];
      const file = input && input.files && input.files[0];

      if (file.size > 20 * 1024 * 1024) {
        const mb = (file.size / 1024 / 1024).toFixed(1);
        this.openSnackbar(`Размер файла ${mb} МБ превышает лимит 20 МБ.`, true);
        input.value = '';
        return;
      }

      const allowedExtensions = ['pdf', 'docx', 'xlsx', 'jpg', 'jpeg', 'png'];
      const extension = (file.name.split('.').pop() || '').toLowerCase();
      if (!allowedExtensions.includes(extension)) {
        this.openSnackbar('Недопустимый формат. Разрешены: PDF, DOCX, XLSX, JPG, PNG.', true);
        input.value = '';
        return;
      }

      this.loading = true
      if (file) {
        const uploadedFile = await uploadFile(file)
        this.attachments.push({
          id: uploadedFile.id,
          name: uploadedFile.name,
        })
        this.attachmentsModifiedDate = new Date();
      }

      this.$refs['attachmentInput'].value = ""
      this.$forceUpdate()
      this.loading = false
    },
    async deleteAttachment(attachment) {
      this.attachments.splice(this.attachments.findIndex(e => e.id === attachment.id), 1);
      this.$forceUpdate()
    },
    async downloadAttachment(attachment) {
      this.loaderDialog = true
      await downloadFile(attachment.id, attachment.name)
      this.loaderDialog = false
    },
    isCommentRequired(indicatorId) {
      const v = this.values[indicatorId];
      return v === '1' || v === '2';
    },
    commentError(indicatorId) {
      return !!this.validation.comments[indicatorId];
    },
    validateForm() {
      this.validation.comments = {};
      this.validation.date = !this.date;
      this.validation.percent = !this.isPercentValid();

      const sections = this.config.sections;
      for (const section of sections) {
        for (const indicator of section.indicators) {
          const id = indicator.id;
          if (this.isCommentRequired(id)) {
            const text = (this.comments[id] || '').trim();
            if (!text) {
              this.$set(this.validation.comments, id, true);
            }
          }
        }
      }

      const hasErrors =
          this.validation.date ||
          this.validation.percent ||
          Object.values(this.validation.comments).some(Boolean);
      return !hasErrors;
    },
    isPercentValid() {
      if (this.autoPercent) return true;
      const percent = this.percent;
      if (percent === null || percent === '') return false;
      const num = Number(percent);
      if (!isFinite(num)) return false;
      return num >= 0 && num <= 100;
    },
    openSnackbar(text, error = false) {
      this.snackbar = true;
      this.snackbarText = text;
      this.snackbarError = error;
    },
  },
  watch: {
    values: {
      handler(newValues) {
        for (const indicatorId in newValues) {
          if (newValues[indicatorId] === '0') {
            this.resetComment(indicatorId);
            this.$delete(this.validation.comments, indicatorId);
          } else {
            if (this.isCommentRequired(indicatorId)) {
              const value = (this.comments[indicatorId] || '').trim();
              this.$set(this.validation.comments, indicatorId, !value);
            } else {
              this.$delete(this.validation.comments, indicatorId);
            }
          }
        }
      },
      deep: true,
    },
    comments: {
      handler(newComments) {
        for (const [indicatorId, value] of Object.entries(newComments)) {
          if (this.isCommentRequired(indicatorId)) {
            this.$set(this.validation.comments, indicatorId, !(value || '').trim());
          } else {
            this.$delete(this.validation.comments, indicatorId);
          }
        }
      },
      deep: true,
    },
    date(val) {
      if (val) this.validation.date = false;
    },
  },
  filters: {
    dateFormat: function (date) {
      if (date == null || date === "") {
        return '—'
      }
      let mdate = new Date(date)
      let options = {timeZone: 'Europe/Moscow', year: 'numeric', month: 'numeric', day: 'numeric'}
      return mdate.toLocaleDateString('ru-RU', options)
    }
  },
  async beforeMount() {
    await this.initialize()
  }
}
</script>

<style lang="scss">
@import "../../styles/main.scss";

.badge {
  width: 152px;
  height: 60px;
  background: #E8E8E9;
  border: 1px solid #E8E8E9;
  border-radius: 4px;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: $cblack;
  margin: 0;
}

.checklist-table {
  .section-row td {
    background: #f7f7f8;
  }
}

.checklist-radio {
  display: grid;
  grid-template-columns: 100px 100px 100px;
  justify-items: center;
  align-items: center;
  width: 100%;
}

.error-text {
  color: #B00020;
  font-size: 12px;
}
</style>


